import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { Spinner } from 'src/components/spinner'
import { CustomSelect } from 'src/components/custom-select'
import { Modal } from 'src/components/modal'
import { OrgPicker } from 'src/components/org-picker'
import { useEligibleBillingOrgs, STATEMENT_BILLING } from 'src/hooks/use-eligible-billing-orgs'
import { useUser } from 'src/services/user'
import { StatementStepIndicator } from 'src/components/statements/statement-step-indicator'
import { wizardStepEnum, WizardStep } from 'src/services/statements/wizard'
import { useAsyncJobStore } from 'src/stores/async-jobs'
import { taskTypeEnum, buildOptimisticJob } from 'src/services/async-jobs/models'
import { statementQueries } from 'src/services/statements/queries'
import { paymentAccountQueries } from 'src/services/payment-accounts/queries'
import { axiosInstance } from 'src/services/axios-instance'
import { SelectOption } from 'src/services/base-model'
import { usePermissions } from 'src/hooks/use-permissions'
import { cn } from 'src/utils/style'
import { InfoHint } from 'src/components/info-hint'

const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
}

// Before a statement exists we cannot derive the applicable steps, so the
// indicator shows the guaranteed spine (Upload always leads to Account,
// Unmatched and Finish) with Upload current and nothing else navigable.
const NEW_STATEMENT_STEPS: WizardStep[] = [
  wizardStepEnum.UPLOAD,
  wizardStepEnum.ACCOUNT,
  wizardStepEnum.UNMATCHED,
  wizardStepEnum.FINISH,
]

// Pull a human-readable message out of a DRF validation/error response so the
// server's rejection (unsupported type, PDF without AI, ineligible billing org) is
// surfaced verbatim on the Upload step.
function extractServerError(error: unknown): string | null {
  if (!error) return null
  const err = error as { response?: { data?: unknown }; message?: string }
  const data = err.response?.data
  if (data == null) return err.message ?? null
  if (typeof data === 'string') return data
  if (typeof data === 'object') {
    const record = data as Record<string, unknown>
    const preferred =
      record.detail ??
      record.non_field_errors ??
      record.statement_file ??
      record.payment_account ??
      record.billing_organization ??
      Object.values(record)[0]
    if (typeof preferred === 'string') return preferred
    if (Array.isArray(preferred) && preferred.length > 0) return String(preferred[0])
  }
  return err.message ?? null
}

export const StatementUpload = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const restoreActive = useAsyncJobStore((s) => s.actions.restoreActive)
  const { hasPermission } = usePermissions()
  const canAccessPaymentAccounts = hasPermission('expenses.access')

  const [file, setFile] = useState<File | null>(null)
  const [selectedAccount, setSelectedAccount] = useState<SelectOption[]>([])
  const [paymentAccountSearch, setPaymentAccountSearch] = useState('')
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false)
  const [billingOrgId, setBillingOrgId] = useState('')

  // AI matching spends wallet credits, so an org must pay for it.
  const { data: user } = useUser()
  const { eligibleMemberships, isLoading: isLoadingWallets } =
    useEligibleBillingOrgs(STATEMENT_BILLING)
  const aiOff = !isLoadingWallets && eligibleMemberships.length === 0
  // PDFs need AI extraction; without a billing org the server refuses them.
  const isPdf = Boolean(file?.name.toLowerCase().endsWith('.pdf'))

  const { data: paymentAccounts } = useQuery({
    ...paymentAccountQueries.list({ filters: { search: paymentAccountSearch } }),
    enabled: canAccessPaymentAccounts,
  })

  const paymentAccountOpts = useMemo(
    () =>
      paymentAccounts?.results.map((account) => ({
        label: account.name,
        value: account.id,
      })) || [],
    [paymentAccounts?.results],
  )

  const uploadMutation = useMutation({
    mutationFn: async (billingOrganization: string | null) => {
      const formData = new FormData()
      formData.append('statement_file', file as File)
      if (billingOrganization) formData.append('billing_organization', billingOrganization)
      // The account is optional: it can be settled later in the Account step.
      if (selectedAccount[0]?.value) formData.append('payment_account', selectedAccount[0].value)
      const response = await axiosInstance.post('/expenses/statements/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data as { id: string; async_job_id?: string }
    },
    onSuccess: (data) => {
      setIsBillingModalOpen(false)
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      // Light up the processing spinner immediately on the review page.
      if (data.async_job_id) {
        restoreActive(
          buildOptimisticJob({
            id: data.async_job_id,
            taskType: taskTypeEnum.STATEMENT_PROCESSING,
            entityId: data.id,
            entityType: 'Statement',
            label: t('asyncJobs.statementProcessing'),
          }),
        )
      }
      navigate(`/expenses/statements/${data.id}/review`)
    },
    // Surface the rejection on the page, not behind the modal.
    onError: () => setIsBillingModalOpen(false),
  })

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) setFile(acceptedFiles[0])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    multiple: false,
  })

  const serverError = extractServerError(uploadMutation.error)
  const canUpload =
    Boolean(file) && !uploadMutation.isPending && !isLoadingWallets && !(aiOff && isPdf)

  const handleUpload = () => {
    if (eligibleMemberships.length === 0) return uploadMutation.mutate(null)
    if (eligibleMemberships.length === 1)
      return uploadMutation.mutate(eligibleMemberships[0].organizationId)
    // Several orgs could pay: ask which one first. Preselect the default org
    // only when it is itself eligible.
    const defaultOrg = user?.defaultOrganization
    setBillingOrgId(
      defaultOrg && eligibleMemberships.some((m) => m.organizationId === defaultOrg)
        ? defaultOrg
        : '',
    )
    setIsBillingModalOpen(true)
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">
          {t('statementUpload.title')}
          <InfoHint text={t('statementUpload.subtitle')} className="ml-1.5" />
        </h1>
      </div>

      {/* Wizard indicator: Upload is the current (first) step. */}
      <div className="border-y border-border py-3">
        <StatementStepIndicator
          steps={NEW_STATEMENT_STEPS}
          currentStep={wizardStepEnum.UPLOAD}
          maxNavigableIndex={0}
        />
      </div>

      <Modal
        isOpen={isBillingModalOpen}
        onClose={() => setIsBillingModalOpen(false)}
        title={t('billing.selectOrganization')}
      >
        <div className="space-y-4" data-testid="billing-org-modal">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">
              {t('billing.selectOrganization')}
              <InfoHint text={t('statementUpload.billedTo')} className="ml-1.5" />
            </label>
            <OrgPicker
              memberships={eligibleMemberships}
              value={billingOrgId || null}
              onChange={setBillingOrgId}
              placeholder={t('billing.selectOrganization')}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setIsBillingModalOpen(false)}
              disabled={uploadMutation.isPending}
              data-testid="billing-org-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => uploadMutation.mutate(billingOrgId)}
              disabled={!billingOrgId || uploadMutation.isPending}
              data-testid="billing-org-confirm"
            >
              {uploadMutation.isPending ? <Spinner size="xs" /> : t('statementUpload.uploadBtn')}
            </Button>
          </div>
        </div>
      </Modal>

      <div className="space-y-4" data-testid="upload-step">
        {canAccessPaymentAccounts && (
          <div data-testid="payment-account-field">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('statementUpload.paymentAccountOptional')}
            </label>
            <CustomSelect
              searchable
              options={paymentAccountOpts}
              values={selectedAccount}
              onChange={(values) => setSelectedAccount(values)}
              placeholder={t('statementsPage.selectAccount')}
              data-testid="payment-account-select"
              searchFn={({ state }) => {
                setPaymentAccountSearch(state.search)
                return paymentAccountOpts
              }}
            />
          </div>
        )}

        <div
          {...getRootProps()}
          className={cn(
            'cursor-pointer rounded-md border-2 border-dashed p-6 text-center transition-colors',
            isDragActive ? 'border-primary-400 bg-surface' : 'border-border-strong',
          )}
          data-testid="statement-dropzone"
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p className="text-sm text-primary-600">{t('statementsPage.dropzoneActive')}</p>
          ) : (
            <p className="text-sm text-text-placeholder">{t('statementsPage.dropzoneText')}</p>
          )}
          <p className="mt-1 text-xs text-text-tertiary">{t('statementsPage.dropzoneFormats')}</p>
        </div>

        {file && (
          <div className="flex items-center justify-between rounded-lg bg-surface-hover p-3">
            <span className="truncate text-sm text-gray-700">{file.name}</span>
            <Button
              variant="ghost"
              tone="error"
              size="sm"
              iconOnly
              tooltip={t('statementUpload.removeFileTooltip')}
              onClick={() => setFile(null)}
              className="ml-2"
              type="button"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        )}

        {aiOff && (
          <p
            className="rounded-md bg-warning-50 p-3 text-sm text-text-secondary"
            data-testid="ai-off-note"
          >
            {t('statementUpload.aiOff')}{' '}
            <Link
              to="/organization-settings#billing"
              className="font-medium text-primary underline"
            >
              {t('statementUpload.billingSettings')}
            </Link>
          </p>
        )}

        {/* Server rejection is shown inline; the user stays on the Upload step. */}
        {serverError && (
          <p
            className="rounded-md bg-error-50 p-3 text-sm text-error-700"
            data-testid="upload-error"
          >
            {serverError}
          </p>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/statements')}
            data-testid="upload-cancel-button"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!canUpload}
            data-testid="upload-statement-button"
          >
            {uploadMutation.isPending ? (
              <Spinner size="xs" />
            ) : (
              <>
                <ArrowUpTrayIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                {t('statementUpload.uploadBtn')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export const StatementUploadWrapper = () => {
  return <StatementUpload />
}
